import { useEffect, useState } from "react";
import Modal, { ModalControlProps } from "../../layouts/Modal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { IToolField } from "../../models/Tools/ToolModels";
import ToolRunConfirmation from "./ToolRunConfirmation";

interface ToolActionModalProps extends ModalControlProps {
  title: string;
  description: string;
  warningText: string;
  fields: IToolField[];
  values: Record<string, unknown>;
  isSubmitting?: boolean;
  onChange: (name: string, value: unknown) => void;
  onSubmit: () => void;
}

export default function ToolActionModal({
  open,
  setOpen,
  title,
  description,
  warningText,
  fields,
  values,
  isSubmitting = false,
  onChange,
  onSubmit,
}: ToolActionModalProps) {
  const [confirmChecked, setConfirmChecked] = useState(false);

  useEffect(() => {
    if (!open) {
      setConfirmChecked(false);
    }
  }, [open]);

  return (
    <Modal open={open} setOpen={setOpen} width="sm:max-w-2xl">
      <div className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <p className="text-sm text-gray-500 mt-1">{description}</p>
        </div>

        <div className="grid grid-cols-1 gap-4">
          {fields.map((field) => {
            const value = values[field.name];

            if (field.type === "checkbox") {
              return (
                <label key={field.name} className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={Boolean(value)}
                    onChange={(e) => onChange(field.name, e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                  />
                  {field.label}
                </label>
              );
            }

            if (field.type === "textarea") {
              return (
                <div key={field.name}>
                  <label className="text-sm font-medium text-gray-600">{field.label}</label>
                  <textarea
                    value={(value as string) ?? ""}
                    placeholder={field.placeholder}
                    required={field.required}
                    onChange={(e) => onChange(field.name, e.target.value)}
                    rows={3}
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              );
            }

            if (field.type === "select") {
              return (
                <div key={field.name}>
                  <label className="text-sm font-medium text-gray-600">{field.label}</label>
                  <select
                    value={(value as string) ?? ""}
                    required={field.required}
                    onChange={(e) => onChange(field.name, e.target.value)}
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">Select an option</option>
                    {(field.options ?? []).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              );
            }

            return (
              <div key={field.name}>
                <label className="text-sm font-medium text-gray-600">{field.label}</label>
                <input
                  value={(value as string | number) ?? ""}
                  type={field.type}
                  placeholder={field.placeholder}
                  required={field.required}
                  onChange={(e) => {
                    const nextValue = field.type === "number" ? Number(e.target.value) : e.target.value;
                    onChange(field.name, nextValue);
                  }}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
            );
          })}
        </div>

        <ToolRunConfirmation
          checked={confirmChecked}
          onCheckedChange={setConfirmChecked}
          warningText={warningText}
        />

        <div className="flex gap-2 pt-2">
          <ButtonInput
            label="Cancel"
            isSubmit={false}
            onClick={() => setOpen(false)}
            classes="!bg-white !text-gray-700 !border !border-gray-300 hover:!bg-gray-50"
          />
          <ButtonInput
            label={isSubmitting ? "Running..." : "Run Tool"}
            isSubmit={false}
            disabled={!confirmChecked || isSubmitting}
            onClick={onSubmit}
            classes="min-w-28"
          />
        </div>
      </div>
    </Modal>
  );
}
